import {
  Alert,
  Card,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Tab,
  Tabs,
  Button,
} from "@mui/material";
import { CalendarMonth } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import theme from "components/utils/theme";
import { TOPICS } from "components/utils/enums";
import getSocket from "../../../redux/saga/socket";
import { getUserSessionsByDeviceServ } from "../../../redux/services/single.service";
import { devicesActions } from "../../../redux/slice/devices.slice";
import { useLocation, useParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styles from "./userActivity.module.scss";

const getSessionStartValue = (session) =>
  session?.startedAt || session?.createdAt || session?.lastSeenAt || null;

const UserActivity = () => {
  const location = useLocation();
  const { userId } = useParams();
  const locationStateUser = location.state?.user;
  const userDetails = useMemo(() => locationStateUser || {}, [
    locationStateUser,
  ]);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState(null);

  const selectedDevice = useSelector(
    devicesActions.selectors.getSelectedDevice
  );
  const selectedDeviceId = useMemo(() => {
    if (userDetails?.deviceId) {
      return userDetails.deviceId;
    }
    if (!selectedDevice) return "";
    return (
      selectedDevice?._id ||
      selectedDevice?.deviceId ||
      selectedDevice?.id ||
      ""
    );
  }, [selectedDevice, userDetails]);

  useEffect(() => {
    if (!selectedDeviceId || !userId) {
      setSessions([]);
      setSessionsError(null);
      setSessionsLoading(false);
      return undefined;
    }

    let isActive = true;
    setSessionsLoading(true);
    setSessionsError(null);

    const fetchSessions = async () => {
      try {
        const response = await getUserSessionsByDeviceServ({
          deviceId: selectedDeviceId,
          userId,
          mode: "list",
        });
        if (!isActive) return;

        const nextSessions = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
          ? response.data
          : response?.data || [];

        setSessions(nextSessions);
      } catch (error) {
        if (!isActive) return;
        setSessions([]);
        setSessionsError(error);
      } finally {
        if (!isActive) return;
        setSessionsLoading(false);
      }
    };

    fetchSessions();

    return () => {
      isActive = false;
    };
  }, [selectedDeviceId, userId]);

  const sortedSessions = useMemo(() => {
    const getSessionDay = (session) =>
      dayjs(getSessionStartValue(session) || 0);
    return [...sessions].sort((a, b) =>
      getSessionDay(b).diff(getSessionDay(a))
    );
  }, [sessions]);

  const sessionDates = useMemo(() => {
    const dates = Array.from(
      new Set(
        sortedSessions
          .map((session) => getSessionStartValue(session))
          .filter(Boolean)
          .map((value) => dayjs(value).format("YYYY-MM-DD"))
      )
    );
    return dates.sort((a, b) => dayjs(b).diff(dayjs(a)));
  }, [sortedSessions]);

  const sessionDateSet = useMemo(() => new Set(sessionDates), [sessionDates]);

  const [selectedDate, setSelectedDate] = useState(sessionDates[0] || "");
  const [tabIndex, setTabIndex] = useState(0);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [liveActivities, setLiveActivities] = useState([]);
  const [minutesSinceLastSeen, setMinutesSinceLastSeen] = useState(0);
  const dateButtonRef = useRef(null);

  useEffect(() => {
    const socketInstance = getSocket();
    if (!socketInstance) {
      return undefined;
    }

    const handleUserActions = (payload = {}) => {
      const message = payload?.message ?? payload;
      let parsed = message;

      if (typeof message === "string") {
        try {
          parsed = JSON.parse(message);
        } catch (error) {
          console.log("USER_ACTIONS", payload);
          return;
        }
      }

      if (!parsed || typeof parsed !== "object") {
        console.log("USER_ACTIONS", payload);
        return;
      }

      const { signature, ...eventData } = parsed;
      if (!eventData || !Object.keys(eventData).length) {
        return;
      }

      const eventTimestamp =
        eventData?.activity?.timestamp || dayjs().toISOString();
      const eventId = `${
        eventData.uid || "event"
      }-${eventTimestamp}-${Date.now()}`;

      setLiveActivities((prev) => {
        const next = [
          {
            id: eventId,
            timestamp: eventTimestamp,
            payload: eventData,
          },
          ...prev,
        ];

        return next.slice(0, 25);
      });

      console.log("USER_ACTIONS", eventData);
    };

    socketInstance.on(TOPICS.USER_ACTIONS, handleUserActions);

    return () => {
      socketInstance.off(TOPICS.USER_ACTIONS, handleUserActions);
    };
  }, []);

  const selectedDateLabel = useMemo(() => {
    if (!selectedDate) {
      return "Select a date";
    }
    return dayjs(selectedDate).format("MMM D, YYYY");
  }, [selectedDate]);

  useEffect(() => {
    if (!sessionDates.length) {
      if (selectedDate) {
        setSelectedDate("");
      }
      if (tabIndex !== 0) {
        setTabIndex(0);
      }
      if (isCalendarOpen) {
        setIsCalendarOpen(false);
      }
      return;
    }

    if (!selectedDate || !sessionDateSet.has(selectedDate)) {
      setSelectedDate(sessionDates[0]);
      setTabIndex(0);
    }
  }, [sessionDates, sessionDateSet, selectedDate, tabIndex, isCalendarOpen]);

  const filteredSessions = useMemo(() => {
    if (!selectedDate) {
      return [];
    }
    return sortedSessions.filter(
      (session) => {
        const start = getSessionStartValue(session);
        return start
          ? dayjs(start).format("YYYY-MM-DD") === selectedDate
          : false;
      }
    );
  }, [sortedSessions, selectedDate]);

  useEffect(() => {
    if (!filteredSessions.length) {
      if (tabIndex !== 0) {
        setTabIndex(0);
      }
      return;
    }

    if (tabIndex >= filteredSessions.length) {
      setTabIndex(0);
    }
  }, [filteredSessions, tabIndex]);

  const activeSession = filteredSessions[tabIndex] || filteredSessions[0];

  const displayName = userDetails?.username || userDetails?.email || userId;
  const liveActivityCount = liveActivities.length;
  const sessionActivities = activeSession?.activities || [];
  const sessionActivityCount = sessionActivities.length;
  const totalActivityCount = liveActivityCount + sessionActivityCount;
  const hasActiveActivities = totalActivityCount > 0;

  useEffect(() => {
    const lastSeenValue = activeSession?.lastSeenAt;
    if (!lastSeenValue) {
      setMinutesSinceLastSeen(0);
      return undefined;
    }

    const updateMinutes = () => {
      const lastSeenMoment = dayjs(lastSeenValue);
      if (!lastSeenMoment.isValid()) {
        setMinutesSinceLastSeen(0);
        return;
      }

      const diff = dayjs().diff(lastSeenMoment, "minute");
      setMinutesSinceLastSeen(diff >= 0 ? diff : 0);
    };

    updateMinutes();
    const intervalId = setInterval(updateMinutes, 60 * 1000);
    return () => clearInterval(intervalId);
  }, [activeSession?.lastSeenAt]);

  const handleChangeTab = (_, value) => {
    setTabIndex(value);
  };

  const handleDateChange = (value) => {
    if (!value) {
      return false;
    }

    const formatted = value.startOf("day").format("YYYY-MM-DD");
    if (!sessionDateSet.has(formatted)) {
      return false;
    }

    if (formatted !== selectedDate) {
      setSelectedDate(formatted);
      setTabIndex(0);
    }

    return true;
  };

  const handleOpenCalendar = () => {
    if (sessionDates.length) {
      setIsCalendarOpen(true);
    }
  };

  const handleCloseCalendar = () => {
    if (isCalendarOpen) {
      setIsCalendarOpen(false);
    }
  };

  const renderSessionMeta = (session) => {
    if (!session) return null;

    const startValue = getSessionStartValue(session);
    const startMoment = startValue ? dayjs(startValue) : null;
    const endMoment = session.lastSeenAt
      ? dayjs(session.lastSeenAt)
      : startMoment;
    const sessionWindow = startMoment && endMoment
      ? `${startMoment.format("MMM D, YYYY • HH:mm")} — ${endMoment.format(
          "HH:mm"
        )}`
      : "—";

    const meta = [
      {
        label: "Session Window",
        value: sessionWindow,
      },
      {
        label: "Time Since Last Seen",
        value: `${minutesSinceLastSeen} minutes`,
      },
      {
        label: "Session ID",
        value: session.sessionId || "—",
      },
      {
        label: "Device ID",
        value: session.deviceId || "—",
      },
    ];

    return (
      <div className={styles.sessionMeta}>
        {meta.map((item) => (
          <div key={item.label} className={styles.metaItem}>
            <Typography variant="caption" color="text.secondary">
              {item.label}
            </Typography>
            <Typography variant="body2" color="text.primary">
              {item.value}
            </Typography>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div className={styles.headerInfo}>
          <Typography variant="h4" fontWeight={600}>
            SecureBoot Activity Overview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Session history and interactions for {displayName}
          </Typography>
        </div>
      </div>

      <Card variant="outlined" sx={{ padding: 3 }}>
        {sessionsLoading && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
            Loading session history…
          </Typography>
        )}
        {sessionsError && (
          <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
            Unable to load session history. {sessionsError?.message || "Please try again."}
          </Alert>
        )}
        <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
          <Stack spacing={1} flex={1}>
            <Typography variant="subtitle2" color="text.secondary">
              User
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {displayName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {userDetails?.email || "No email on file"}
            </Typography>
          </Stack>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: "none", md: "block" } }}
          />
          <Stack spacing={1} flex={1}>
            <Typography variant="subtitle2" color="text.secondary">
              Device ID
            </Typography>
            <Typography variant="body2" color="text.primary">
              {userDetails?.deviceId || "690b4696f738b27ed1080051"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Username: {userDetails?.username || "N/A"}
            </Typography>
          </Stack>
        </Stack>
      </Card>

      <Card variant="outlined" sx={{ padding: 3 }}>
        {sessionDates.length ? (
          <div className={styles.dateSelectorRow}>
            <div className={styles.dateSummary}>
              <Typography variant="subtitle1" fontWeight={600}>
                Sessions on {selectedDateLabel}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filteredSessions.length} session
                {filteredSessions.length === 1 ? "" : "s"} recorded.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use the tabs to switch between sessions captured on this date.
              </Typography>
            </div>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                className={styles.hiddenPicker}
                value={selectedDate ? dayjs(selectedDate) : null}
                onChange={(value) => {
                  if (handleDateChange(value)) {
                    handleCloseCalendar();
                  }
                }}
                open={isCalendarOpen}
                onClose={handleCloseCalendar}
                disableFuture
                minDate={
                  sessionDates.length
                    ? dayjs(sessionDates[sessionDates.length - 1])
                    : undefined
                }
                maxDate={
                  sessionDates.length ? dayjs(sessionDates[0]) : undefined
                }
                shouldDisableDate={(date) =>
                  !sessionDateSet.has(date.startOf("day").format("YYYY-MM-DD"))
                }
                slotProps={{
                  textField: {
                    sx: { display: "none" },
                  },
                  popper: {
                    anchorEl: dateButtonRef.current,
                    placement: "bottom-start",
                  },
                }}
              />
            </LocalizationProvider>
            <Button
              variant="outlined"
              className={styles.dateButton}
              onClick={handleOpenCalendar}
              ref={dateButtonRef}
              startIcon={<CalendarMonth />}
            >
              {selectedDateLabel}
            </Button>
          </div>
        ) : (
          <div className={styles.emptyState}>
            <Typography variant="body2">
              No SecureBoot sessions recorded yet for this user.
            </Typography>
          </div>
        )}

        {filteredSessions.length ? (
          <Tabs
            value={tabIndex}
            onChange={handleChangeTab}
            variant="scrollable"
            allowScrollButtonsMobile
            className={styles.sessionTabs}
          >
            {filteredSessions.map((session, index) => {
              const startTime =
                getSessionStartValue(session) || session.createdAt || session.lastSeenAt;
              return (
                <Tab
                  key={session.sessionId || `session-${index}`}
                  value={index}
                  label={dayjs(startTime || 0).format("HH:mm")}
                />
              );
            })}
          </Tabs>
        ) : null}

        {activeSession ? (
          <Stack spacing={3} mt={filteredSessions.length ? 3 : 0}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Chip
                label={activeSession?.status || "Session"}
                className={styles.badge}
                sx={{
                  backgroundColor: "rgba(76, 175, 80, 0.12)",
                  color: theme.palette.success.main,
                }}
              />
              <Typography variant="body2" color="text.secondary">
                Session ID: {activeSession?.sessionId || "—"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Duration {minutesSinceLastSeen} mins • {sessionActivityCount} recorded actions
                {liveActivityCount
                  ? ` • ${liveActivityCount} live event${
                      liveActivityCount === 1 ? "" : "s"
                    }`
                  : ""}
              </Typography>
            </Stack>
            {renderSessionMeta(activeSession)}
            {hasActiveActivities ? (
              <TableContainer
                component={Paper}
                className={styles.activityTable}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={220}>Timestamp</TableCell>
                      <TableCell width={180}>Action</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {liveActivities.map((event) => {
                      const timeLabel = dayjs(event.timestamp).isValid()
                        ? dayjs(event.timestamp).format(
                            "MMM D, YYYY • HH:mm:ss"
                          )
                        : event.timestamp;

                      return (
                        <TableRow key={`live-${event.id}`}>
                          <TableCell>{timeLabel}</TableCell>
                          <TableCell>
                            <Typography fontWeight={600}>
                              USER_ACTIONS
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.5}>
                              {event.payload.uid ? (
                                <Typography variant="body2">
                                  UID: {event.payload.uid}
                                </Typography>
                              ) : null}
                              {event.payload.user ? (
                                <Typography variant="body2">
                                  User: {event.payload.user}
                                </Typography>
                              ) : null}
                              {event.payload.OS ? (
                                <Typography variant="body2">
                                  OS: {event.payload.OS}
                                </Typography>
                              ) : null}
                              {event.payload.activity?.type ? (
                                <Typography variant="body2">
                                  Activity Type: {event.payload.activity.type}
                                </Typography>
                              ) : null}
                              {event.payload.activity?.details ? (
                                <Typography variant="body2">
                                  Details: {event.payload.activity.details}
                                </Typography>
                              ) : null}
                              {event.payload.activity?.timestamp ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Reported Timestamp:{" "}
                                  {event.payload.activity.timestamp}
                                </Typography>
                              ) : null}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {sessionActivities.map((activity, index) => {
                      const structuredPayload = activity.activity;
                      const timestamp = structuredPayload?.timestamp || activity.time;
                      const parsedTimestamp = timestamp && dayjs(timestamp).isValid()
                        ? dayjs(timestamp).format("MMM D, YYYY • HH:mm:ss")
                        : timestamp || "—";
                      const actionLabel = structuredPayload?.type || activity.action || "Activity";

                      const detailContent = structuredPayload ? (
                        <Stack spacing={0.5}>
                          {structuredPayload.details ? (
                            <Typography variant="body2">
                              Details: {structuredPayload.details}
                            </Typography>
                          ) : null}
                          {activity.uid ? (
                            <Typography variant="body2">UID: {activity.uid}</Typography>
                          ) : null}
                          {activity.user ? (
                            <Typography variant="body2">User: {activity.user}</Typography>
                          ) : null}
                          {activity.OS ? (
                            <Typography variant="body2">OS: {activity.OS}</Typography>
                          ) : null}
                        </Stack>
                      ) : (
                        activity.detail
                      );

                      return (
                        <TableRow
                          key={`${activeSession?.sessionId || "session"}-${index}-${timestamp || "activity"}`}
                        >
                          <TableCell>{parsedTimestamp}</TableCell>
                          <TableCell>
                            <Typography fontWeight={600}>{actionLabel}</Typography>
                          </TableCell>
                          <TableCell>{detailContent}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info" className={styles.emptyState}>
                No activity captured for this session yet.
              </Alert>
            )}
          </Stack>
        ) : null}
      </Card>
    </div>
  );
};

export default UserActivity;
